import { Alert, AlertTitle, Box, Button, Stack } from '@mui/material';
import { SerializedError } from '@reduxjs/toolkit';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import BugReportIcon from '@mui/icons-material/BugReport';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import React, { useEffect, useRef } from 'react';
import { useDebugError } from '@contexts/DebugErrorContext';
import useT from '@hooks/useT';
import { DebugErrorInfo } from '@/types/DebugErrorInfo';
import ServerUnavailableError from './ServerUnavailableError';
import NotFoundError from './NotFoundError';
import UnauthorizedError from './UnauthorizedError';
import ForbiddenError from './ForbiddenError';
import GenericServerError from './GenericServerError';

/** Structured error from MCMProblemDetailsFactory (via problemDetails.extensions.errors) */
interface StructuredError {
  code: string;
  description: string;
  metadata?: Record<string, unknown>;
}

interface ErrorData {
  message?: string;
  title?: string;
  detail?: string;
  type?: string;
  /** Validation errors (Record) or structured errors (array) - checked at runtime */
  errors?: unknown;
  debugInfo?: DebugErrorInfo;
}

const DEFAULT_ERROR = 'Une erreur est survenue lors de la dernière action.';

// Helper: Check if error message indicates connection issues
function isConnectionError(error: FetchBaseQueryError | SerializedError): boolean {
  if ('message' in error && error.message) {
    const connectionErrors = ['ECONNREFUSED', 'Failed to fetch', 'Network request failed'];
    return connectionErrors.some(err => error.message?.includes(err));
  }
  return false;
}

// Helper: Check if status indicates server unavailability
function isServerUnavailable(status: number | string): boolean {
  if (status === 'FETCH_ERROR' || status === 'PARSING_ERROR') return true;
  return status === 502 || status === 503 || status === 504;
}

// Helper: Check if 500 error is a connection issue
function is500ConnectionError(error: FetchBaseQueryError): boolean {
  if ('status' in error && error.status === 500) {
    const data = error.data;
    return data === null || typeof data === 'string';
  }
  return false;
}

// Helper: Get component for HTTP status code
function getStatusErrorComponent(status: number | string): React.ReactElement | null {
  if (status === 404) return <NotFoundError />;
  if (status === 401) return <UnauthorizedError />;
  if (status === 403) return <ForbiddenError />;
  if (typeof status === 'number' && status >= 500) return <GenericServerError />;
  return null;
}

// Helper: Parse error message with optional metadata (format: "Main message|key1=value1|key2=value2")
// Interpolates placeholders like {key} in the message with metadata values
// If no placeholders found, just strips the metadata and returns the clean message
function parseErrorMessage(message: string): { main: string } {
  const parts = message.split('|');
  if (parts.length === 1) {
    return { main: message };
  }

  let main = parts[0];
  const metadataParts = parts.slice(1);

  // Build metadata dictionary and interpolate placeholders
  for (const part of metadataParts) {
    const eqIndex = part.indexOf('=');
    if (eqIndex === -1) continue;
    const key = part.substring(0, eqIndex);
    const value = part.substring(eqIndex + 1);
    main = main.replace(`{${key}}`, value);
  }

  return { main };
}

// Helper: Check if errors are in structured format (array of {code, description, metadata})
function isStructuredErrors(errors: unknown): errors is StructuredError[] {
  return (
    Array.isArray(errors) &&
    errors.length > 0 &&
    typeof errors[0] === 'object' &&
    errors[0] !== null &&
    'code' in errors[0]
  );
}

// Helper: Check if errors are in validation format (Record<string, string[]>)
function isValidationErrors(errors: unknown): errors is Record<string, string[]> {
  if (typeof errors !== 'object' || errors === null || Array.isArray(errors)) {
    return false;
  }
  const values = Object.values(errors);
  for (const value of values) {
    if (!Array.isArray(value)) return false;
    for (const item of value) {
      if (typeof item !== 'string') return false;
    }
  }
  return true;
}

// Helper: Translate structured error using i18next with metadata interpolation
function translateStructuredError(
  error: StructuredError,
  translate: (key: string, options?: Record<string, unknown>) => string
): string {
  // Try to look up translation by error code
  const translationKey = error.code;
  const options: Record<string, unknown> = { defaultValue: '' };
  if (error.metadata) {
    Object.assign(options, error.metadata);
  }
  const translated = translate(translationKey, options);

  // If translation exists and is different from the key, use it
  if (translated && translated !== translationKey) {
    return translated;
  }

  // Fall back to description with manual interpolation for {key} placeholders
  let result = error.description;
  if (error.metadata) {
    for (const [key, value] of Object.entries(error.metadata)) {
      result = result.replace(`{${key}}`, String(value));
    }
  }
  return result;
}

// Helper: Render validation errors (Record<string, string[]> format)
function renderValidationErrors(errors: Record<string, string[]>): React.ReactElement {
  return (
    <Stack spacing={1.5}>
      {Object.keys(errors).map(code => {
        const fullMessage = errors[code].join(', ');
        const { main } = parseErrorMessage(fullMessage);
        return (
          <Alert
            key={code}
            severity="error"
            icon={<ErrorOutlineIcon />}
            sx={{
              borderRadius: 2,
              '& .MuiAlert-message': { width: '100%' },
            }}
          >
            <Box sx={{ fontWeight: 500 }}>{main}</Box>
          </Alert>
        );
      })}
    </Stack>
  );
}

type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

// Helper: Render structured errors (StructuredError[] format) with i18n support
function StructuredErrorsDisplay({
  errors,
  action,
}: Readonly<{
  errors: StructuredError[];
  action?: React.ReactNode;
}>): React.ReactElement {
  const { t } = useT();
  // Cast t to simple function type to avoid TypeScript overload resolution issues
  const translate = t as TranslateFn;

  if (errors.length === 1) {
    // Single error - render as simple alert
    const message = translateStructuredError(errors[0], translate);
    return (
      <Alert
        severity="error"
        icon={<ErrorOutlineIcon />}
        action={action}
        sx={{
          borderRadius: 2,
          '& .MuiAlert-message': { width: '100%' },
        }}
      >
        <Box sx={{ fontWeight: 500 }}>{message}</Box>
      </Alert>
    );
  }

  // Multiple errors - render as stack
  return (
    <Stack spacing={1.5}>
      {errors.map((error, index) => {
        const message = translateStructuredError(error, translate);
        return (
          <Alert
            key={error.code + index}
            severity="error"
            icon={<ErrorOutlineIcon />}
            action={index === 0 ? action : undefined}
            sx={{
              borderRadius: 2,
              '& .MuiAlert-message': { width: '100%' },
            }}
          >
            <Box sx={{ fontWeight: 500 }}>{message}</Box>
          </Alert>
        );
      })}
    </Stack>
  );
}

// Helper: Render message/title error
function renderMessageError(
  message?: string,
  title?: string,
  action?: React.ReactNode
): React.ReactElement {
  const hasTitle = Boolean(title);
  const hasMessage = Boolean(message);

  // Parse and interpolate metadata if present
  const displayMessage = message ? parseErrorMessage(message).main : undefined;
  const displayTitle = title ? parseErrorMessage(title).main : undefined;

  return (
    <Alert
      severity="error"
      icon={<ErrorOutlineIcon />}
      action={action}
      sx={{
        borderRadius: 2,
        '& .MuiAlert-message': { width: '100%' },
      }}
    >
      {hasTitle && hasMessage && <AlertTitle sx={{ fontWeight: 600 }}>{displayTitle}</AlertTitle>}
      <Box sx={{ fontWeight: hasTitle && hasMessage ? 400 : 500 }}>
        {displayMessage ?? displayTitle ?? DEFAULT_ERROR}
      </Box>
    </Alert>
  );
}

// Helper: Handle status-based errors
function handleStatusError(error: FetchBaseQueryError): React.ReactElement | null {
  const { status } = error;

  if (isServerUnavailable(status) || is500ConnectionError(error)) {
    return <ServerUnavailableError />;
  }

  return getStatusErrorComponent(status);
}

// Helper: Extract debug info from error
function extractDebugInfo(error: FetchBaseQueryError | SerializedError): DebugErrorInfo | undefined {
  if ('data' in error && error.data && typeof error.data === 'object') {
    const data = error.data as ErrorData;
    return data.debugInfo;
  }
  return undefined;
}

// Helper: Extract status code from error
function extractStatusCode(error: FetchBaseQueryError | SerializedError): number {
  if ('status' in error && typeof error.status === 'number') {
    return error.status;
  }
  return 500; // Default to 500 for unknown errors
}

// Helper: Extract error message from error
function extractErrorMessage(error: FetchBaseQueryError | SerializedError): string {
  if ('data' in error && error.data && typeof error.data === 'object') {
    const data = error.data as ErrorData;
    return data.detail ?? data.message ?? data.title ?? DEFAULT_ERROR;
  }
  if ('message' in error && error.message) {
    return error.message;
  }
  return DEFAULT_ERROR;
}

function ApiError({ error }: Readonly<{ error: FetchBaseQueryError | SerializedError | undefined }>) {
  const { t } = useT();
  const { isDebugEnabled, showDebugError } = useDebugError();
  const hasShownModalRef = useRef<string | null>(null);

  const debugInfo = error ? extractDebugInfo(error) : undefined;
  const statusCode = error ? extractStatusCode(error) : 0;
  const errorMessage = error ? extractErrorMessage(error) : '';

  // Auto-popup for 500+ errors (only once per unique error)
  useEffect(() => {
    if (!isDebugEnabled || !debugInfo || statusCode < 500) return;

    // Create a unique key for this error to avoid showing the same modal twice
    const errorKey = `${statusCode}-${debugInfo.timestamp}`;
    if (hasShownModalRef.current === errorKey) return;

    hasShownModalRef.current = errorKey;
    showDebugError(statusCode, errorMessage, debugInfo);
  }, [isDebugEnabled, debugInfo, statusCode, errorMessage, showDebugError]);

  if (!error) return null;

  if (isConnectionError(error)) {
    return <ServerUnavailableError />;
  }

  // Build debug action button for 4xx errors
  const showDebugButton = isDebugEnabled && debugInfo && statusCode >= 400 && statusCode < 500;
  const debugAction = showDebugButton ? (
    <Button
      startIcon={<BugReportIcon />}
      size="small"
      color="inherit"
      onClick={() => showDebugError(statusCode, errorMessage, debugInfo)}
      sx={{ whiteSpace: 'nowrap' }}
    >
      {t('debug.showDetails')}
    </Button>
  ) : undefined;

  // Check for status-based errors first (these have their own components)
  if ('status' in error) {
    const statusComponent = handleStatusError(error);
    if (statusComponent) {
      return statusComponent;
    }
  }

  // Handle data-based errors with optional debug action
  if ('data' in error && error.data) {
    const data = error.data as ErrorData;

    // Check for structured errors first (from MCMProblemDetailsFactory)
    // These have code + metadata for proper i18n interpolation
    if (data.errors && isStructuredErrors(data.errors)) {
      return <StructuredErrorsDisplay errors={data.errors} action={debugAction} />;
    }

    // Validation errors in Record<string, string[]> format
    if (data.type === 'validation' && data.errors && isValidationErrors(data.errors)) {
      return renderValidationErrors(data.errors);
    }

    if (data.message || data.title) {
      return renderMessageError(data.message, data.title, debugAction);
    }
  }

  return renderMessageError(undefined, undefined, debugAction);
}

export default ApiError;
